import React, { useEffect, useState } from "react";
import {
  Drawer,
  Button,
  Upload,
  Space,
  Typography,
  message,
  Divider,
  Select,
  Spin,
} from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { ROLL_CALL_API_BASE_URL } from "../../RollCall/RollCallConfig/RollCallConfig";

const { Text } = Typography;
const { Option } = Select;

const UploadBulkVisitor = ({ open, onClose }) => {
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [UserId, setUserId] = useState(null);
  const [file, setFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");
    const userId = JSON.parse(
      sessionStorage.getItem("userData") || "{}",
    ).UserId;
    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
      setUserId(userId);
    } else {
      console.warn("User data not found in sessionStorage");
      navigate("/signin");
    }
  }, [navigate]);

  const fetchEvents = async () => {
    try {
      setEventsLoading(true);

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}Visitor/Pass/getVisitorEvents?SuperId=${superIdData}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${tokenData}`,
            "Content-Type": "application/json",
          },
        },
      );

      const data = await response.json();

      if (data?.status) {
        const eventList = data.ResultData || [];

        setEvents(eventList);
        if (eventList.length > 0) {
          setSelectedEventId(eventList[0].Id);
        }
      }
    } catch (error) {
      console.error("Error fetching visitor events:", error);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [superIdData, tokenData]);

  const clearStates = () => {
    setFile(null);
    setUploadLoading(false);
    setTemplateLoading(false);
  };

  const handleClose = () => {
    clearStates();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      clearStates();
    }
  }, [open]);

  const downloadTemplate = async () => {
    try {
      setTemplateLoading(true);

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}Visitor/Pass/getVisitorBulkTemplate`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to download template");
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "Visitor_Bulk_Template.xlsx";
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      message.success("Template downloaded successfully");
    } catch (error) {
      console.error(error);
      message.error("Failed to download template");
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      message.warning("Please select an Excel file");
      return;
    }

    try {
      setUploadLoading(true);
      const selectedEvent = events.find(
        (event) => event.Id === selectedEventId,
      );

      const eventDate = selectedEvent?.EventDate;
      const formData = new FormData();

      formData.append("file", file);

      formData.append("SuperId", superIdData);
      formData.append("EventId", selectedEventId);
      formData.append("StartDateTime", `${eventDate}`);
      formData.append("EndDateTime", `${eventDate}`);
      formData.append("VisitType", "6");
      formData.append("UserId", UserId);

      const response = await fetch(
        "${ ROLL_CALL_API_BASE_URL }Visitor/Pass/uploadBulkVisitors",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenData}`,
          },
          body: formData,
        },
      );

      const result = await response.json();

      if (!response.ok || result?.status === false) {
        throw new Error(result?.message || "Upload failed");
      }

      message.success(result?.message || "Visitors uploaded successfully");

      handleClose();
    } catch (error) {
      console.error(error);
      message.error(error.message || "Upload failed");
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <Drawer
      title="Bulk Visitor Upload"
      placement="right"
      width={450}
      open={open}
      onClose={handleClose}
      destroyOnClose
    >
      {uploadLoading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" tip="Uploading visitors..." />
        </div>
      )}
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Text strong>Step 1: Download Template</Text>

          <Divider style={{ margin: "10px 0" }} />

          <Button
            icon={<DownloadOutlined />}
            loading={templateLoading}
            onClick={downloadTemplate}
            style={{
              backgroundColor: "#6c757d",
              borderColor: "#6c757d",
              color: "#fff",
            }}
          >
            Download Template
          </Button>
        </div>

        {/* Upload File */}
        <div>
          <Text strong>Step 2: Upload Filled Template</Text>

          <Divider style={{ margin: "10px 0" }} />
          <div className="d-flex">
            <div className="d-flex flex-column ms-2 text-start">
              <label className="form-label">File</label>
              <Upload
                accept=".xlsx,.xls"
                maxCount={1}
                beforeUpload={(selectedFile) => {
                  setFile(selectedFile);
                  return false;
                }}
                onRemove={() => {
                  setFile(null);
                }}
              >
                <Button icon={<UploadOutlined />}>Select Excel File</Button>
              </Upload>
            </div>
            <div className="d-flex flex-column ms-2 text-start">
              <label className="form-label">Event</label>
              <Select
                placeholder="Select Event"
                style={{ width: 170 }}
                value={selectedEventId}
                onChange={(value) => {
                  setSelectedEventId(value);
                }}
                loading={eventsLoading}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {events.map((event) => (
                  <Option key={event.Id} value={event.Id}>
                    {event.Name}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          {file && (
            <div className="mt-2">
              <Text type="secondary">Selected: {file.name}</Text>
            </div>
          )}
        </div>
      </Space>
      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="btn btn-secondary me-2"
          onClick={handleClose}
        >
          Cancel
        </button>
        <button
          className="btn btn-success"
          disabled={!file}
          onClick={handleUpload}
        >
          Submit
        </button>
      </div>
    </Drawer>
  );
};
UploadBulkVisitor.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UploadBulkVisitor;
